import {
    Avatar,
    Box,
    Button,
    Collapse,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography
} from "@mui/material";
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import React, { useState } from "react";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { SUPERSET_COLORS } from "../../utils/exerciseUtils";
import { ManipulationTypes } from "../../enums/ManipulationTypes";
import { TempoTypes } from "../../enums/TempoTypes";
import { RTL } from "../../cache/CacheRtl";
import { imageUrl } from "../../utils/imageUrl";

const SuperSetGroup = React.memo(({
    exerciseA,
    exerciseB,
    readOnly,
    onUnlink,
    onRemoveBoth,
    onExerciseUpdate,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const displayNameA = exerciseA.exerciseName || exerciseA.name || exerciseA.nameHeb || 'Unknown';
    const displayNameB = exerciseB.exerciseName || exerciseB.name || exerciseB.nameHeb || 'Unknown';

    // Shared fields: sets and rest time (always from exerciseA, synced)
    const sharedSets = exerciseA.numberOfSets || 3;
    const sharedRest = exerciseA.restTimeInSec || 120;

    const handleSharedFieldChange = (field, value) => {
        if (field === 'numberOfSets') {
            onExerciseUpdate({ ...exerciseA, numberOfSets: value });
            onExerciseUpdate({ ...exerciseB, numberOfSets: value });
        } else if (field === 'restTimeInSec') {
            onExerciseUpdate({ ...exerciseA, restTimeInSec: value });
            onExerciseUpdate({ ...exerciseB, restTimeInSec: value });
        }
    };

    const handleExerciseFieldChange = (exercise, field, value) => {
        const fieldMapping = {
            reps: 'recommendedReps',
            manipulation: 'manipulation',
            tempo: 'tempo',
            notes: 'comment',
        };
        const prop = fieldMapping[field] || field;
        onExerciseUpdate({ ...exercise, [prop]: value });
    };

    // Per-set range handlers
    const handleAddSetRange = (exercise) => {
        const setRanges = exercise.setRanges || [];
        const newSetRange = { setIndex: setRanges.length, minReps: null, maxReps: null };
        onExerciseUpdate({ ...exercise, setRanges: [...setRanges, newSetRange] });
    };

    const handleRemoveSetRange = (exercise, indexToRemove) => {
        const setRanges = exercise.setRanges || [];
        const updatedRanges = setRanges
            .filter((_, idx) => idx !== indexToRemove)
            .map((range, idx) => ({ ...range, setIndex: idx }));
        onExerciseUpdate({
            ...exercise,
            setRanges: updatedRanges.length > 0 ? updatedRanges : undefined
        });
    };

    const handleSetRangeChange = (exercise, setIndex, field, value) => {
        const setRanges = exercise.setRanges || [];
        const updatedRanges = setRanges.map((range, idx) =>
            idx === setIndex ? { ...range, [field]: value } : range
        );
        onExerciseUpdate({ ...exercise, setRanges: updatedRanges });
    };

    const handleToggleExpand = (e) => {
        if (e.target.closest('.MuiTextField-root') || e.target.closest('.MuiSelect-root')) return;
        setIsExpanded(!isExpanded);
    };

    const renderExerciseConfig = (exercise, label) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                color: SUPERSET_COLORS.text,
            }}>
                {label}
            </Typography>

            {/* Reps */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
                        חזרות
                    </Typography>
                    <TextField
                        type="number"
                        value={exercise.recommendedReps || 10}
                        onChange={(e) => handleExerciseFieldChange(exercise, 'reps', parseInt(e.target.value) || 0)}
                        size="small"
                        fullWidth
                        inputProps={{ min: 1, max: 999 }}
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
                        מניפולציה
                    </Typography>
                    <RTL>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={exercise.manipulation || "RP_NEW"}
                                onChange={(e) => handleExerciseFieldChange(exercise, 'manipulation', e.target.value)}
                                sx={{ backgroundColor: 'white', borderRadius: 1.5 }}
                            >
                                {ManipulationTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </RTL>
                </Box>
            </Box>

            {/* Tempo */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
                    קצב תנועה
                </Typography>
                <RTL>
                    <FormControl size="small" fullWidth>
                        <Select
                            value={exercise.tempo || "T1_0_1_0"}
                            onChange={(e) => handleExerciseFieldChange(exercise, 'tempo', e.target.value)}
                            sx={{ backgroundColor: 'white', borderRadius: 1.5 }}
                        >
                            {TempoTypes.map((tempo) => (
                                <MenuItem key={tempo.value} value={tempo.value}>{tempo.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </RTL>
            </Box>

            {/* Notes */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
                    הערות
                </Typography>
                <TextField
                    multiline
                    rows={2}
                    value={exercise.comment || ""}
                    onChange={(e) => handleExerciseFieldChange(exercise, 'notes', e.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 },
                        '& .MuiInputBase-input': { textAlign: 'right', direction: 'rtl' }
                    }}
                    placeholder="הערות..."
                />
            </Box>

            {/* Per-Set Ranges */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        טווח חזרות לפי סט
                    </Typography>
                    <IconButton onClick={() => handleAddSetRange(exercise)} size="small" sx={{ color: SUPERSET_COLORS.accent }}>
                        <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                </Box>
                {exercise.setRanges && exercise.setRanges.length > 0 && (
                    <Box sx={{ backgroundColor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                        <Box sx={{
                            display: 'grid', gridTemplateColumns: '32px 1fr 1fr 32px', gap: 0,
                            alignItems: 'center', p: '6px 8px', borderBottom: '1px solid #E2E8F0', backgroundColor: '#F1F5F9'
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>סט</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>מינימום</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>מקסימום</Typography>
                            <Box />
                        </Box>
                        {exercise.setRanges.map((setRange, idx) => (
                            <Box key={idx} sx={{
                                display: 'grid', gridTemplateColumns: '32px 1fr 1fr 32px', gap: 0,
                                alignItems: 'center', p: '4px 8px',
                                borderBottom: idx < exercise.setRanges.length - 1 ? '1px solid #E2E8F0' : 'none'
                            }}>
                                <Box sx={{
                                    width: 22, height: 22, borderRadius: '50%', backgroundColor: SUPERSET_COLORS.accent,
                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.7rem', fontWeight: 700, mx: 'auto'
                                }}>
                                    {idx + 1}
                                </Box>
                                <Box sx={{ px: 0.5 }}>
                                    <TextField
                                        type="number" value={setRange.minReps || ''} size="small" placeholder="6"
                                        onChange={(e) => handleSetRangeChange(exercise, idx, 'minReps', e.target.value ? parseInt(e.target.value) : null)}
                                        inputProps={{ min: 1, max: 999, style: { textAlign: 'center', padding: '4px 6px' } }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1 } }}
                                    />
                                </Box>
                                <Box sx={{ px: 0.5 }}>
                                    <TextField
                                        type="number" value={setRange.maxReps || ''} size="small" placeholder="12"
                                        onChange={(e) => handleSetRangeChange(exercise, idx, 'maxReps', e.target.value ? parseInt(e.target.value) : null)}
                                        inputProps={{ min: 1, max: 999, style: { textAlign: 'center', padding: '4px 6px' } }}
                                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1 } }}
                                    />
                                </Box>
                                <IconButton onClick={() => handleRemoveSetRange(exercise, idx)} size="small" sx={{ color: '#94A3B8', p: '2px', '&:hover': { color: 'error.main' } }}>
                                    <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <Paper
            elevation={2}
            sx={{
                backgroundColor: SUPERSET_COLORS.background,
                borderRight: `4px solid ${SUPERSET_COLORS.accent}`,
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    elevation: 4,
                    transform: 'translateY(-1px)',
                }
            }}
        >
            {/* Compact Header (always visible) - matches ExerciseItem height */}
            <Box
                onClick={handleToggleExpand}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    gap: 1.5,
                }}
            >
                {/* Two stacked images */}
                <Box sx={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
                    <Avatar
                        src={imageUrl(exerciseA.imageUrl || exerciseA.coverUrl, 26)}
                        variant="rounded"
                        sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '6px',
                            backgroundColor: '#EDE9FE',
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            border: `2px solid ${SUPERSET_COLORS.background}`,
                            zIndex: 2,
                        }}
                    >
                        <FitnessCenterIcon sx={{ color: SUPERSET_COLORS.badgeA, fontSize: 12 }} />
                    </Avatar>
                    <Avatar
                        src={imageUrl(exerciseB.imageUrl || exerciseB.coverUrl, 26)}
                        variant="rounded"
                        sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '6px',
                            backgroundColor: '#EDE9FE',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            border: `2px solid ${SUPERSET_COLORS.background}`,
                            zIndex: 1,
                        }}
                    >
                        <FitnessCenterIcon sx={{ color: SUPERSET_COLORS.badgeB, fontSize: 12 }} />
                    </Avatar>
                </Box>

                {/* Names stacked */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: SUPERSET_COLORS.badgeA, fontWeight: 700, flexShrink: 0 }}>א'</Typography>
                        <Typography sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#1E293B',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                        }}>
                            {displayNameA}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: SUPERSET_COLORS.badgeB, fontWeight: 700, flexShrink: 0 }}>ב'</Typography>
                        <Typography sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#1E293B',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                        }}>
                            {displayNameB}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', lineHeight: 1.2, mt: 0.25 }}>
                        סופר סט · {sharedSets} סטים · {sharedRest} שניות מנוחה
                    </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                    {!readOnly && (
                        <>
                            <IconButton
                                onClick={(e) => { e.stopPropagation(); onUnlink(); }}
                                size="small"
                                sx={{
                                    color: '#94A3B8', p: 0.5,
                                    '&:hover': { color: 'error.main' },
                                }}
                            >
                                <LinkOffIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                                onClick={(e) => { e.stopPropagation(); onRemoveBoth(); }}
                                size="small"
                                sx={{
                                    color: '#94A3B8', p: 0.5,
                                    '&:hover': { color: 'error.main' },
                                }}
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </>
                    )}
                    <IconButton
                        size="small"
                        sx={{
                            color: SUPERSET_COLORS.accent, p: 0.5,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                        }}
                    >
                        <ExpandMoreOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Expanded Configuration */}
            <Collapse in={isExpanded}>
                <Box sx={{
                    px: 2,
                    py: 2,
                    backgroundColor: '#FAFBFC',
                    direction: 'rtl',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    ...(readOnly && { opacity: 0.7, pointerEvents: 'none' }),
                }}>
                    {/* Shared fields */}
                    <Box sx={{
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 2,
                        border: '1px solid #E2E8F0'
                    }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1E293B', mb: 1.5, textAlign: 'right' }}>
                            הגדרות משותפות
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
                                    מספר סטים
                                </Typography>
                                <TextField
                                    type="number"
                                    value={sharedSets}
                                    onChange={(e) => handleSharedFieldChange('numberOfSets', parseInt(e.target.value) || 1)}
                                    size="small"
                                    fullWidth
                                    inputProps={{ min: 1, max: 20 }}
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textAlign: 'right' }}>
                                    זמן מנוחה (שניות)
                                </Typography>
                                <TextField
                                    type="number"
                                    value={sharedRest}
                                    onChange={(e) => handleSharedFieldChange('restTimeInSec', parseInt(e.target.value) || 0)}
                                    size="small"
                                    fullWidth
                                    inputProps={{ min: 0, max: 9999 }}
                                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white', borderRadius: 1.5 } }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Exercise A config */}
                    <Box sx={{
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 2,
                        border: `1px solid ${SUPERSET_COLORS.border}`,
                        borderRight: `3px solid ${SUPERSET_COLORS.badgeA}`,
                    }}>
                        {renderExerciseConfig(exerciseA, `תרגיל א' - ${displayNameA}`)}
                    </Box>

                    {/* Exercise B config */}
                    <Box sx={{
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 2,
                        border: `1px solid ${SUPERSET_COLORS.border}`,
                        borderRight: `3px solid ${SUPERSET_COLORS.badgeB}`,
                    }}>
                        {renderExerciseConfig(exerciseB, `תרגיל ב' - ${displayNameB}`)}
                    </Box>

                    {/* Delete both button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                        <RTL>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={onRemoveBoth}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    px: 3,
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    '&:hover': {
                                        backgroundColor: 'error.main',
                                        color: 'white',
                                        borderColor: 'error.main',
                                    }
                                }}
                            >
                                מחק סופר סט
                            </Button>
                        </RTL>
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    );
});

export default SuperSetGroup;
